import { useState } from "react";
import { Plus, Pencil, Trash2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { SignedImg } from "@/lib/media";

const ROLE = { storyteller: "Storyteller", recipient: "Exchanging recordings", reference: "Reference only" };

function PersonForm({ people, person, onDone, onCancel }) {
  const [f, setF] = useState({
    name: person?.name || "", familiar_name: person?.familiar_name || "", relationship: person?.relationship || "",
    generation: person?.generation ?? 1, parent_ids: person?.parent_ids || [], partner_ids: person?.partner_ids || [],
  });
  const others = people.filter((p) => p.id !== person?.id);
  const toggle = (k, id) => setF({ ...f, [k]: f[k].includes(id) ? f[k].filter((x) => x !== id) : [...f[k], id] });
  const label = (p) => `${p.name}${p.familiar_name ? ` (“${p.familiar_name}”)` : ""}${p.relationship ? ` · ${p.relationship}` : ""}`;
  const save = async () => {
    try {
      const body = { ...f, generation: Number(f.generation) };
      if (person) await api.put(`/story2/tree/people/${person.id}`, body); else await api.post("/story2/tree/people", body);
      onDone();
    } catch (e) { toast.error(apiError(e)); }
  };
  const canName = !person || person.role === "reference";
  return (
    <div className="glass-card p-6 mb-6 space-y-4" data-testid="person-form">
      <p className="font-serif text-2xl text-[#EDE6F7]">{person ? `Edit ${person.name}` : "Add a relative for reference"}</p>
      {!person && <p className="text-sm text-[#A49CB8]">Reference-only relatives help the story explain who's who. They don't use a recipient space, aren't invited and don't record anything.</p>}
      <div className="grid sm:grid-cols-2 gap-4">
        {canName && <label className="block"><span className="text-[#C9C2D8]">Name</span>
          <input data-testid="person-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1 w-full h-12 rounded-lg bg-[#1A1222] border border-[#33264A] px-3 text-[#EDE6F7]" /></label>}
        <label className="block"><span className="text-[#C9C2D8]">Familiar name (optional, e.g. Nana)</span>
          <input data-testid="person-familiar" value={f.familiar_name} onChange={(e) => setF({ ...f, familiar_name: e.target.value })} className="mt-1 w-full h-12 rounded-lg bg-[#1A1222] border border-[#33264A] px-3 text-[#EDE6F7]" /></label>
        <label className="block"><span className="text-[#C9C2D8]">Relationship to the storyteller</span>
          <input data-testid="person-relationship" value={f.relationship} placeholder="e.g. Daughter, Stepson, Partner, Godmother" onChange={(e) => setF({ ...f, relationship: e.target.value })} className="mt-1 w-full h-12 rounded-lg bg-[#1A1222] border border-[#33264A] px-3 text-[#EDE6F7]" /></label>
        <label className="block"><span className="text-[#C9C2D8]">Generation</span>
          <select data-testid="person-generation" value={f.generation} onChange={(e) => setF({ ...f, generation: e.target.value })} className="mt-1 w-full h-12 rounded-lg bg-[#1A1222] border border-[#33264A] px-3 text-[#EDE6F7]">
            <option value={0}>Grandparents</option><option value={1}>Their children</option><option value={2}>Grandchildren</option><option value={3}>Great-grandchildren</option>
          </select></label>
      </div>
      {[["parent_ids", "Parents (choose up to two)"], ["partner_ids", "Partner"]].map(([k, t]) => (
        <fieldset key={k}><legend className="text-[#C9C2D8] mb-2">{t}</legend>
          <div className="flex flex-wrap gap-2">{others.map((p) => (
            <label key={p.id} className={`px-3 py-2 rounded-full border cursor-pointer text-sm ${f[k].includes(p.id) ? "border-[#8B2CFF] text-[#EDE6F7] bg-[#1E1428]" : "border-[#33264A] text-[#A49CB8]"}`}>
              <input type="checkbox" className="sr-only" checked={f[k].includes(p.id)} onChange={() => toggle(k, p.id)} data-testid={`person-${k}-${p.id}`} />{label(p)}
            </label>))}</div>
        </fieldset>
      ))}
      <div className="flex gap-3">
        <button onClick={save} data-testid="person-save-btn" className="gold-btn h-12 px-6 rounded-full">Save</button>
        <button onClick={onCancel} data-testid="person-cancel-btn" className="ghost-gold h-12 px-6 rounded-full">Cancel</button>
      </div>
    </div>
  );
}

function PersonCard({ p, by, onEdit, onDelete }) {
  const parents = (p.parent_ids || []).map((x) => by[x]?.name).filter(Boolean);
  const partners = (p.partner_ids || []).map((x) => by[x]?.name).filter(Boolean);
  return (
    <div className={`glass-card p-4 w-full sm:w-60 ${p.role === "reference" ? "border-dashed" : ""}`} data-testid={`tree-person-${p.id}`}>
      <div className="flex items-center gap-3">
        {p.photo ? <SignedImg path={p.photo} alt="" className="h-12 w-12 rounded-full object-cover" /> :
          <div className="h-12 w-12 rounded-full bg-[#1E1428] border border-[#3B2D52] flex items-center justify-center font-serif text-xl text-[#B57BFF]">{p.name[0]}</div>}
        <div className="min-w-0">
          <p className="text-[#EDE6F7] text-lg truncate">{p.name}{p.familiar_name ? ` · “${p.familiar_name}”` : ""}</p>
          <p className="text-xs text-[#A49CB8]">{p.relationship || "Relationship not set"}</p>
        </div>
      </div>
      <p className={`mt-2 text-xs ${p.role === "reference" ? "text-[#C9B6E4]" : "text-[#B57BFF]"}`} data-testid={`tree-role-${p.id}`}>{ROLE[p.role]}</p>
      {parents.length > 0 && <p className="text-xs text-[#A49CB8] mt-1">Child of {parents.join(" and ")}</p>}
      {partners.length > 0 && <p className="text-xs text-[#A49CB8]">Partner of {partners.join(" and ")}</p>}
      <div className="flex gap-2 mt-3">
        <button onClick={onEdit} aria-label={`Edit ${p.name}`} data-testid={`tree-edit-${p.id}`} className="h-9 px-3 rounded-full border border-[#33264A] text-[#C9C2D8] text-sm flex items-center"><Pencil className="h-4 w-4 mr-1" />Edit</button>
        {p.role === "reference" && <button onClick={onDelete} aria-label={`Remove ${p.name}`} data-testid={`tree-delete-${p.id}`} className="h-9 px-3 rounded-full border border-[#33264A] text-[#C9C2D8] text-sm flex items-center"><Trash2 className="h-4 w-4 mr-1" />Remove</button>}
      </div>
    </div>
  );
}

export function FamilyTree({ tree, reload }) {
  const [editing, setEditing] = useState(null);
  const by = Object.fromEntries(tree.people.map((p) => [p.id, p]));
  const gens = [0, 1, 2, 3].filter((g) => tree.people.some((p) => p.generation === g));
  const done = () => { setEditing(null); reload(); };
  const remove = async (p) => { try { await api.delete(`/story2/tree/people/${p.id}`); reload(); } catch (e) { toast.error(apiError(e)); } };
  const confirm = async () => { try { await api.post("/story2/tree/confirm"); toast.success("Family tree confirmed."); reload(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <section data-testid="family-tree">
      <p className="text-[#B9B3C8] mb-6">Fill in as much as you can — no dates of birth needed. 1. Check the storyteller. 2. Add relatives who help explain the story. 3. Connect children to their parents. The people exchanging recordings are already here.</p>
      {editing && <PersonForm people={tree.people} person={editing === "new" ? null : editing} onDone={done} onCancel={() => setEditing(null)} />}
      {gens.map((g) => (
        <div key={g} className="mb-8" data-testid={`tree-generation-${g}`}>
          <p className="eyebrow mb-3">{tree.generations[g]}</p>
          <div className="flex flex-wrap gap-3">{tree.people.filter((p) => p.generation === g).map((p) => (
            <PersonCard key={p.id} p={p} by={by} onEdit={() => setEditing(p)} onDelete={() => remove(p)} />))}</div>
        </div>
      ))}
      <div className="flex flex-wrap gap-3 items-center">
        <button onClick={() => setEditing("new")} data-testid="tree-add-btn" className="ghost-gold h-12 px-6 rounded-full flex items-center"><Plus className="h-4 w-4 mr-2" />Add a relative for reference</button>
        <button onClick={confirm} data-testid="tree-confirm-btn" className="gold-btn h-12 px-6 rounded-full">{tree.confirmed_at ? "Confirm again" : "These relationships are correct"}</button>
        {tree.confirmed_at && <span className="flex items-center gap-2 text-[#D9C6FF]" data-testid="tree-confirmed"><CheckCircle2 className="h-5 w-5" />Confirmed</span>}
      </div>
    </section>
  );
}
